import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export interface WalletOption {
  id: string;
  name: string;
  vendor: string;
  active: boolean;
  loadingModel?: 'PREPAID' | 'PER_BOOKING';
}

type LoadingModel = 'PREPAID' | 'PER_BOOKING';

/**
 * Wallet picker with an in-place "Add wallet" affordance. Replaces the
 * native `<select>` on the wallet booking form so ops can create a new
 * B2B wallet (Klook, Expedia, whichever comes up next) without waiting
 * on a dev to run a SQL insert.
 *
 * Ships as a searchable dropdown for the existing list plus a bottom
 * row that opens a compact modal to POST /api/wallets. On save the
 * dropdown refreshes and auto-selects the new wallet so the operator
 * can fall straight through into the amount + submit.
 */
export function WalletSelect({
  wallets,
  value,
  onChange,
  className,
  mode = 'BOOKING',
}: {
  wallets: WalletOption[];
  value: string;
  onChange: (walletId: string) => void;
  className?: string;
  /** 'BOOKING' shows all active wallets (both loading models can log
   *  a booking). 'LOAD' shows only PREPAID wallets because per-booking
   *  wallets aren't loaded — every booking bills the shared account
   *  directly. */
  mode?: 'BOOKING' | 'LOAD';
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onClickAway = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClickAway);
    return () => window.removeEventListener('mousedown', onClickAway);
  }, []);

  const activeWallets = useMemo(
    () => wallets.filter((w) => w.active && (mode === 'BOOKING' || w.loadingModel !== 'PER_BOOKING')),
    [wallets, mode],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return activeWallets;
    return activeWallets.filter(
      (w) => w.name.toLowerCase().includes(term) || w.vendor.toLowerCase().includes(term),
    );
  }, [q, activeWallets]);

  const selected = value ? activeWallets.find((w) => w.id === value) : null;

  return (
    <div ref={ref} className={cn('relative w-full', className)}>
      <input
        ref={inputRef}
        type="text"
        value={open ? q : selected?.name ?? ''}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQ(''); }}
        placeholder="— Select wallet —"
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      {open && (
        <div
          className="absolute left-0 z-50 mt-1 min-w-full w-max max-w-[420px] overflow-hidden rounded-lg border border-border bg-white text-foreground shadow-xl ring-1 ring-black/5 dark:bg-neutral-900"
          role="listbox"
        >
          <div className="max-h-[240px] overflow-y-auto py-1">
            {filtered.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => { onChange(w.id); setOpen(false); setQ(''); inputRef.current?.blur(); }}
                className={cn(
                  'flex w-full items-center justify-between gap-3 whitespace-nowrap px-3 py-2 text-left text-sm transition-colors hover:bg-muted/70',
                  value === w.id && 'bg-primary/8',
                )}
                role="option"
                aria-selected={value === w.id}
              >
                <span className={cn('font-medium', value === w.id ? 'text-primary' : 'text-foreground/90')}>
                  {w.name}
                </span>
                {w.loadingModel === 'PER_BOOKING' && (
                  <span className="rounded bg-muted/70 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    per-booking
                  </span>
                )}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-muted-foreground">
                No match for &ldquo;{q}&rdquo;. Add it below.
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setShowAddModal(true); setOpen(false); }}
            className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm font-medium text-primary hover:bg-accent"
          >
            <span className="text-base leading-none">+</span>
            <span>Add a wallet to this list…</span>
          </button>
        </div>
      )}

      {showAddModal && (
        <AddWalletModal
          onClose={() => setShowAddModal(false)}
          onCreated={(id) => { onChange(id); setShowAddModal(false); }}
        />
      )}
    </div>
  );
}

function AddWalletModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [loadingModel, setLoadingModel] = useState<LoadingModel>('PREPAID');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api<WalletOption>('/api/wallets', {
        method: 'POST',
        body: {
          name: name.trim(),
          loadingModel,
          // Per-booking wallets have no meaningful opening balance —
          // send 0 regardless of what the (hidden) field held.
          openingBalance: loadingModel === 'PREPAID' ? openingBalance : '0',
          currency: 'INR',
          notes: notes.trim() || undefined,
        },
      }),
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['wallets'] });
      onCreated(created.id);
    },
    onError: (e: Error) => setErr(e.message),
  });

  const canSubmit =
    name.trim().length > 0 &&
    (loadingModel === 'PER_BOOKING' || /^-?\d+(\.\d{1,2})?$/.test(openingBalance));

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-black/45 p-4 pt-16 sm:pt-24"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl ring-1 ring-black/5 dark:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">Add wallet</h3>
            <p className="text-xs text-muted-foreground">
              B2B booking wallet — Klook, Expedia, TBO, and the like.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xl text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="wname">Wallet name</Label>
            <Input
              id="wname"
              value={name}
              onChange={(e) => { setName(e.target.value); setErr(null); }}
              placeholder="Klook Tech / Expedia / …"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Loading model</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <ModelOption
                selected={loadingModel === 'PREPAID'}
                title="Prepaid"
                sub="I load funds via card, spend from balance (TBO, Razorpay)"
                onClick={() => setLoadingModel('PREPAID')}
              />
              <ModelOption
                selected={loadingModel === 'PER_BOOKING'}
                title="Per-booking"
                sub="No advance load — each booking bills the shared account (Expedia, GRN)"
                onClick={() => setLoadingModel('PER_BOOKING')}
              />
            </div>
          </div>

          {loadingModel === 'PREPAID' && (
            <div>
              <Label htmlFor="wopen">Opening balance (INR)</Label>
              <Input
                id="wopen"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e.target.value.replace(/[^0-9.\-]/g, ''))}
                inputMode="decimal"
                placeholder="0"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Starting balance already on the wallet before any loads/spends are logged in Trawish.
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="wnotes">Notes (optional)</Label>
            <Input
              id="wnotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Klook business partner login for Aashwin"
            />
          </div>

          {err && <p className="text-xs text-destructive">{err}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!canSubmit || create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create wallet'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModelOption({
  selected,
  title,
  sub,
  onClick,
}: {
  selected: boolean;
  title: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-start rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/8 ring-1 ring-primary'
          : 'border-input bg-background hover:bg-muted/50',
      )}
    >
      <span className={cn('text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}>
        {title}
      </span>
      <span className="mt-1 text-[11px] leading-snug text-muted-foreground">
        {sub}
      </span>
    </button>
  );
}
